import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, TextField, Typography } from "@mui/material";
import { FlagIcon } from "src/components/iconify/flagIcon";
import countriesJson from "src/utils/countries.json";
import Iconify from "src/components/iconify";

type Props = {
    rows: any[];
    onChange?: (rows: any[]) => void;
    mode: string;
    errorCellId?: string | null;
};

export default function DataGridCreateStrategy({ rows, onChange, mode, errorCellId }: Props) {
    const baseColumns: GridColDef[] = [
        {
            field: "platform", headerName: "Platform", flex: 1, minWidth: 140,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{params?.value}</Typography>
                )
            }
        },
        {
            field: "account_name", headerName: "Account", flex: 1, minWidth: 180,
            renderCell: (params) => {
                return (
                    <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{params?.value}</Typography>
                )
            }
        },
        {
            field: "campaign_names",
            headerName: "Campaign",
            flex: 1,
            minWidth: 190,
            renderCell: ({ value, row }) => {

                return (
                    <Box display="flex" flexDirection="row" gap={0.5}>
                        {(value || []).map((c: string, i: number) => (
                            <Box key={i} display="flex" alignItems="center" gap={0.5}>
                                <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{c}</Typography>
                            </Box>
                        ))}
                    </Box>
                );
            },
        },
        {
            field: "keyword",
            headerName: "Keyword",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{params?.value}</Typography>
            ),
        },
    ];

    const columns = [
        ...baseColumns,
        {
            field: "ad_group_names", headerName: "Ad Group", flex: 1, minWidth: 200,
            renderCell: (params: any) => (
                <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>{params?.value}</Typography>
            ),
        },
        ...(mode === "KO Bidding Manual"
            ? [
                {
                    field: "max_cpc",
                    headerName: "Max CPC",
                    type: "number",
                    editable: true,
                    minWidth: 60,
                    valueGetter: (params: any) => {
                        const detail = params.row.activations_details?.find(
                            (d: any) => d.mode === "KO Bidding Manual"
                        );
                        return detail?.max_cpc ?? null;
                    },

                    valueSetter: (params: any) => {
                        const { value, row } = params;
                        const updatedDetails = [...(row.activations_details || [])];
                        const index = updatedDetails.findIndex((d) => d.mode === "KO Bidding Manual");

                        if (index >= 0) {
                            updatedDetails[index] = { ...updatedDetails[index], max_cpc: value };
                        } else {
                            updatedDetails.push({ mode: "KO Bidding Manual", max_cpc: value });
                        }

                        return { ...row, activations_details: updatedDetails, max_cpc: value };
                    },

                    renderCell: (params: any) => (
                        <Box
                            sx={{
                                borderRadius: '6px',
                                border: '1px solid rgba(145, 158, 171, 0.16)',
                                display: 'flex',
                                alignItems: 'center',
                                width: "50%",
                                justifyContent: 'center',
                                padding: '6px 8px',
                                '&:hover': {
                                    cursor: 'pointer',
                                    borderColor: '#338AF3',
                                    backgroundColor: 'rgba(51, 138, 243, 0.15)',
                                    boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                }

                            }}
                        >
                            <Typography fontSize={14} color={"rgba(51, 60, 71, 1)"}>
                                {params.value != null ? params.value : "--"}
                            </Typography>
                        </Box>
                    ),

                    cellClassName: (params: any) =>
                        (params.value == null || params.value === "") &&
                            params.id === errorCellId
                            ? "cell-error"
                            : "",
                },
            ]
            : mode === "KO Bidding Auto"
                ? [
                    {
                        field: "strategy",
                        headerName: "KPI",
                        editable: true,
                        flex: 1,
                        minWidth: 100,
                        type: "singleSelect",
                        valueOptions: [
                            { value: "Smart Top", label: "Smart Top" },
                            { value: "Smart First", label: "Smart First" },
                            { value: "Paid Top", label: "Paid Top" },
                            { value: "Paid First", label: "Paid First" },
                        ],

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode === "KO Bidding Auto"
                            );
                            return detail?.strategy ?? "Smart Top";
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;
                            const updatedDetails = [...(row.activations_details || [])];
                            const index = updatedDetails.findIndex((d) => d.mode === "KO Bidding Auto");

                            const selectedValue = value || "Smart Top";

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    strategy: selectedValue
                                };
                            } else {
                                updatedDetails.push({
                                    mode: "KO Bidding Auto",
                                    strategy: selectedValue
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                                strategy: selectedValue
                            };
                        },


                        renderCell: (params: any) => (
                            <Box
                                sx={{
                                    borderRadius: '6px',
                                    border: '1px solid rgba(145, 158, 171, 0.16)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: "100%",
                                    color: "rgba(51, 60, 71, 1)",
                                    justifyContent: 'center',
                                    padding: '6px 8px',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        borderColor: 'rgba(0, 108, 156, 0.40)',
                                        backgroundColor: 'rgba(0, 108, 156, 0.03)',
                                        boxShadow: '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                    }
                                }}
                            >
                                {params.value ?? "--"}
                            </Box>
                        ),
                    },
                    {
                        field: "accepted_variation_threshold",
                        headerName: "Threshold",
                        type: "number",
                        editable: true,
                        flex: 1,
                        minWidth: 120,

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode === "KO Bidding Auto"
                            );
                            return detail?.accepted_variation_threshold ?? null;
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;
                            const updatedDetails = [...(row.activations_details || [])];
                            const index = updatedDetails.findIndex((d) => d.mode === "KO Bidding Auto");

                            // ✅ Clamp entre 1 et 99
                            const clampedValue = Math.max(1, Math.min(99, Number(value) || 0));

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    accepted_variation_threshold: clampedValue,
                                    strategy: updatedDetails[index].strategy || "Smart Top",
                                };
                            } else {
                                updatedDetails.push({
                                    strategy: "Smart Top",
                                    mode: "KO Bidding Auto",
                                    accepted_variation_threshold: clampedValue
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                                accepted_variation_threshold: clampedValue,
                                strategy: updatedDetails[index]?.strategy || "Smart Top",

                            };
                        },
                        renderCell: (params: any) => {
                            const isError = params.id === errorCellId;

                            return (
                                <Box
                                    sx={{
                                        borderRadius: '6px',
                                        border: `2px solid ${isError ? 'red' : 'rgba(145, 158, 171, 0.16)'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: "50%",
                                        color: "rgba(51, 60, 71, 1)",
                                        justifyContent: 'center',
                                        padding: '6px 8px',
                                        backgroundColor: isError ? 'rgba(255,0,0,0.05)' : 'transparent',
                                        '&:hover': {
                                            cursor: 'pointer',
                                            borderColor: isError ? 'red' : 'rgba(0, 108, 156, 0.40)',
                                            backgroundColor: isError ? 'rgba(255,0,0,0.05)' : 'rgba(0, 108, 156, 0.03)',
                                            boxShadow: isError ? '0 0 0 2px rgba(255,0,0,0.2)' : '0 0 0 2px rgba(51, 138, 243, 0.20)',
                                        }
                                    }}
                                >
                                    {params.value != null ? params.value : "--"}
                                </Box>
                            );
                        }

                    },

                    {
                        field: "max_cpc_target_reduction",
                        headerName: "Floor Max CPC",
                        type: "number",
                        editable: true,
                        flex: 1,
                        minWidth: 160,

                        valueGetter: (params: any) => {
                            const detail = params.row.activations_details?.find(
                                (d: any) => d.mode?.startsWith("KO Bidding Auto")
                            );
                            return detail?.max_cpc_target_reduction ?? null;
                        },

                        valueSetter: (params: any) => {
                            const { value, row } = params;

                            const updatedDetails = [...(row.activations_details || [])];

                            const index = updatedDetails.findIndex(
                                (d) => d.mode?.startsWith("KO Bidding Auto")
                            );

                            const numericValue =
                                value === "" || value === null || value === undefined
                                    ? null
                                    : Number(value);

                            // bloque les valeurs négatives
                            if (numericValue !== null && numericValue < 0) {
                                return row;
                            }

                            if (index >= 0) {
                                updatedDetails[index] = {
                                    ...updatedDetails[index],
                                    max_cpc_target_reduction: numericValue,
                                };
                            } else {
                                updatedDetails.push({
                                    mode: "KO Bidding Auto",
                                    strategy: "Smart Top",
                                    max_cpc_target_reduction: numericValue,
                                });
                            }

                            return {
                                ...row,
                                activations_details: updatedDetails,
                            };
                        },

                        renderCell: (params: any) => {
                            const isError = params.value != null && params.value < 0;

                            return (
                                <Box
                                    sx={{
                                        borderRadius: "8px",
                                        border: `2px solid ${isError ? "red" : "rgba(145, 158, 171, 0.16)"}`,
                                        display: "flex",
                                        alignItems: "center",
                                        width: "110px",
                                        height: "36px",
                                        overflow: "hidden",
                                        backgroundColor: isError ? "rgba(255,0,0,0.05)" : "transparent",
                                        "&:hover": {
                                            cursor: "pointer",
                                            borderColor: isError ? "red" : "rgba(0, 108, 156, 0.40)",
                                            backgroundColor: isError
                                                ? "rgba(255,0,0,0.05)"
                                                : "rgba(0, 108, 156, 0.03)",
                                            boxShadow: isError
                                                ? "0 0 0 2px rgba(255,0,0,0.2)"
                                                : "0 0 0 2px rgba(51, 138, 243, 0.20)",
                                        },
                                    }}
                                >
                                    <TextField
                                        value={params.value ?? ""}
                                        type="number"
                                        variant="standard"
                                        placeholder="--"
                                        onChange={(e: any) => {
                                            const val = e.target.value;

                                            if (val === "") {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: "",
                                                });
                                                return;
                                            }

                                            const num = Number(val);

                                            if (num >= 0) {
                                                params.api.setEditCellValue({
                                                    id: params.id,
                                                    field: params.field,
                                                    value: num,
                                                });
                                            }
                                        }}
                                        inputProps={{
                                            min: 0,
                                            step: 0.01,
                                            style: {
                                                textAlign: "right",
                                                padding: "6px 8px",
                                                fontSize: "14px",
                                            },
                                        }}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInput-root:before": { display: "none" },
                                            "& .MuiInput-root:after": { display: "none" },
                                        }}
                                    />
                                </Box>
                            );
                        }
                    }

                ]
                : []),
    ];

    return (
        <Box sx={{ border: '1px solid rgba(145, 158, 171, 0.20)', borderRadius: "12px" }}>
            <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                hideFooter
                getRowId={(r) => r.id ?? `${r.keyword}-${r.country}-${Math.random()}`}
                processRowUpdate={(newRow) => {
                    const updatedRows = rows.map((r) =>
                        r.id === newRow.id ? newRow : r
                    );
                    onChange?.(updatedRows);
                    return newRow;
                }}

                sx={{
                    color: "#FFF",
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#FAFBFB",
                        borderRadius: "12px",
                        color: "rgba(99, 115, 129, 1)",
                        fontWeight: 500,
                    },
                    "& .MuiDataGrid-cell": {
                        borderBottom: "0px",
                    },
                }}
            />
        </Box>
    );
}
